import React, { useDeferredValue } from "react";
import copy from "../../../assets/icons/copy.png";
import dot from "../../../assets/icons/dot.png";
import SearchableDropdown from "../../../components/common/SearchableDropdown";
import { matchesOrderedTokenSearch } from "../../../utils/orderedTokenSearch";

const DRAG_HANDLE_CLASS =
  "flex absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[20%] w-8 h-8 bg-white dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-700 rounded-full items-center justify-center cursor-grab active:cursor-grabbing z-10 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-colors touch-manipulation";

const DRAG_HANDLE_SVG = (
  <svg
    className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300 shrink-0"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

/**
 * Sidebar section for macros: search, dropdown (mobile), and list (desktop).
 */
function MacrosSection({
  listeners,
  hideDragHandle = false,
  macros,
  selectedMacroId,
  onMacroSelect,
  isMacroSearchOpen,
  onMacroSearchToggle,
  macroSearchQuery,
  onMacroSearchQueryChange,
  macroOptions,
  onMacroDropdownChange,
  t,
}) {
  const trimmedSearch = (macroSearchQuery || "").trim();
  const deferredSearchQuery = useDeferredValue(trimmedSearch);

  return (
    <div className="relative p-2 sm:p-3 bg-white dark:bg-gray-800 rounded-2xl">
      {!hideDragHandle && (
        <div
          className={DRAG_HANDLE_CLASS}
          {...listeners}
          style={{ touchAction: "none" }}
          title={t("dragToReorder") || "Drag to reorder"}
          role="button"
          aria-label={t("dragToReorder") || "Drag to reorder"}
        >
          {DRAG_HANDLE_SVG}
        </div>
      )}
      <div className="flex gap-2 justify-between items-start">
        <div className="flex gap-2 flex-1 min-w-0">
          <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gray-200 dark:bg-gray-700 rounded-full place-content-center justify-items-center flex items-center justify-center flex-shrink-0">
            <img
              src={copy}
              alt=""
              className="w-4 h-4 sm:w-5 sm:h-5 dark:invert"
            />
          </div>
          <div className="text-[10px] sm:text-xs flex-1 min-w-0">
            <h1 className="font-bold text-gray-900 dark:text-gray-200 text-sm sm:text-base">
              {t("macros")}
            </h1>
            <h1 className="text-[10px] sm:text-xs text-gray-900 dark:text-gray-200 break-words">
              {t("findYourSavedMacrosHere")}
            </h1>
          </div>
        </div>
        <div className="gap-1 hidden lg:flex flex-shrink-0">
          <button
            className="bg-green dark:bg-green-dark cursor-pointer w-9 h-9 sm:w-10 sm:h-10 rounded-full place-content-center justify-items-center hover:bg-green-dark dark:hover:bg-green flex items-center justify-center"
            onClick={onMacroSearchToggle}
            type="button"
            aria-label={
              isMacroSearchOpen ? "Close macro search" : "Search macros"
            }
          >
            {isMacroSearchOpen ? (
              <svg
                className="w-4 h-4 text-white dark:text-black"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19,6.41 L17.59,5 L12,10.59 L6.41,5 L5,6.41 L10.59,12 L5,17.59 L6.41,19 L12,13.41 L17.59,19 L19,17.59 L13.41,12 L19,6.41 Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4 text-white dark:text-black"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.5,14 L14.71,14 L14.43,13.73 C15.41,12.59 16,11.11 16,9.5 C16,5.91 13.09,3 9.5,3 C5.91,3 3,5.91 3,9.5 C3,13.09 5.91,16 9.5,16 C11.11,16 12.59,15.41 13.73,14.43 L14,14.71 L14,15.5 L19,20.49 L20.49,19 L15.5,14 Z M9.5,14 C7.01,14 5,11.99 5,9.5 C5,7.01 7.01,5 9.5,5 C11.99,5 14,7.01 14,9.5 C14,11.99 11.99,14 9.5,14 Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
      <div className="mt-2 lg:hidden">
        <SearchableDropdown
          label="Select Macro"
          value={selectedMacroId}
          options={macroOptions}
          onChange={onMacroDropdownChange}
          placeholder="Search macros..."
          emptyText="No macros available."
          emptySearchText="No macros match your search."
        />
      </div>
      <div className="hidden lg:block">
        {isMacroSearchOpen && (
          <div className="mt-2 sm:mt-3">
            <input
              type="text"
              value={macroSearchQuery}
              onChange={(e) => onMacroSearchQueryChange(e.target.value)}
              placeholder="Search macros..."
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-200 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green dark:focus:ring-green-dark"
            />
          </div>
        )}
        <div
          className="relative space-y-1 blur-overlay blur-bottom h-[250px] sm:h-[400px] overflow-y-auto custom-scrollbar mobile-scroll"
          style={{ touchAction: "pan-y" }}
        >
          <div
            className="overflow-y-auto h-[200px] sm:h-96 mobile-scroll"
            style={{ touchAction: "pan-y" }}
          >
            {macros
              .filter((macro) => {
                if (!deferredSearchQuery) return true;
                const name = macro?.macroName || "";
                return matchesOrderedTokenSearch(name, deferredSearchQuery);
              })
              .map((macro, index) => (
                <div
                  key={index}
                  onClick={() => onMacroSelect(macro)}
                  style={{ touchAction: "pan-y" }}
                  className="w-full flex gap-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 place-content-center items-center h-auto p-2 sm:p-3 rounded-xl sm:rounded-2xl mt-2 sm:mt-3 cursor-pointer"
                >
                  <div className="w-7 h-7 sm:w-8 sm:h-8 place-content-center justify-items-center bg-black rounded-full flex items-center justify-center flex-shrink-0">
                    <img
                      src={dot}
                      alt=""
                      className="w-2 h-3 dark:invert"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-gray-700 dark:text-gray-200 text-[10px] sm:text-xs ml-1 leading-3 break-words">
                      {macro.macroName}
                    </h1>
                  </div>
                </div>
              ))}
          </div>
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none"></div>
        </div>
      </div>
    </div>
  );
}

export default MacrosSection;
